import React from 'react';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const CATEGORY_OPTIONS = [
    { id: 'SOUP', label: 'Çorba' },
    { id: 'MAIN', label: 'Ana Yemek' },
    { id: 'SALAD', label: 'Salata' },
    { id: 'DESSERT', label: 'Tatlı' }
];

const buildAlakartGenerationErrorHtml = (payload = {}) => {
    const details = Array.isArray(payload?.details) ? payload.details : [];
    if (details.length === 0) {
        return `<p style="margin:0;">${payload?.error || 'Plan oluşturulamadı.'}</p>`;
    }

    const items = details.map((issue) => {
        const currentValue = Number(issue?.currentValue || 0);
        const threshold = Number(issue?.threshold || 0);
        const unit = String(issue?.unit || '').trim();
        const label = String(issue?.label || issue?.message || issue?.type || 'Kural').trim();
        return `
            <li style="margin:0 0 10px 0;">
                <strong>${label}</strong><br/>
                <span>Şu an: ${Number.isFinite(currentValue) ? currentValue : '-'} ${unit}</span><br/>
                <span>Eşik: ${Number.isFinite(threshold) ? threshold : '-'} ${unit}</span>
            </li>
        `;
    }).join('');

    return `
        <div style="text-align:left;">
            <p style="margin:0 0 12px 0;">${payload?.error || 'Sistem planı contract eşiğini geçemedi.'}</p>
            <ul style="margin:0;padding-left:18px;">
                ${items}
            </ul>
            <p style="margin:12px 0 0 0;color:#64748b;">Boş slotla devam etmek istersen "Menüyü Kendim Yapıcam" modunu kullanabilirsin.</p>
        </div>
    `;
};

const GENERATION_STEPS = [
    'Stok kontrolü yapılıyor...',
    'Eksik malzemeler listeleniyor...',
    'Bütçe uyumları kontrol ediliyor...',
    'Kişisel sağlık adımları alınıyor...',
    'Protein dengesi hesaplanıyor...',
    'Plan oluşturuluyor...',
];

const AlakartPhaseZeroSurvey = ({ onComplete, onCancel }) => {
    const [creating, setCreating] = React.useState(false);
    const [stepIndex, setStepIndex] = React.useState(0);
    const [stepVisible, setStepVisible] = React.useState(true);
    const [step, setStep] = React.useState('entry');
    const [generationMode, setGenerationMode] = React.useState('system');
    const [config, setConfig] = React.useState({
        portion: 4,
        categories: ['SOUP', 'MAIN', 'SALAD']
    });

    React.useEffect(() => {
        if (!creating) { setStepIndex(0); setStepVisible(true); return; }
        const interval = setInterval(() => {
            setStepVisible(false);
            setTimeout(() => {
                setStepIndex((prev) => (prev + 1) % GENERATION_STEPS.length);
                setStepVisible(true);
            }, 250);
        }, 2200);
        return () => clearInterval(interval);
    }, [creating]);

    const openPreferences = (mode) => {
        setGenerationMode(mode);
        setStep('preferences');
    };

    const handleCreatePlan = async () => {
        setCreating(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE_URL}/api/alakart-menu/create-plan`, {
                generationMode,
                contextSnapshot: {
                    excluded: [],
                    familyInputs: [],
                    portion: config.portion,
                    targetCategories: config.categories,
                    generationMode
                }
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                const isManual = generationMode === 'manual';
                Swal.fire({
                    icon: 'success',
                    title: isManual ? 'Boş Plan Hazır' : 'Plan Oluşturuldu',
                    text: isManual
                        ? 'Boş tarih slotları hazır. Şimdi menünü kendin doldurabilirsin.'
                        : 'Haftalık menün hazır.',
                    timer: 1600,
                    showConfirmButton: false
                }).then(() => {
                    onComplete?.({
                        plan: response.data.plan,
                        config: {
                            portion: config.portion,
                            categories: config.categories
                        },
                        generationMode
                    });
                });
            }
        } catch (error) {
            console.error('Plan creation error:', error);
            const payload = error?.response?.data || {};
            Swal.fire({
                icon: 'error',
                title: 'Sistem Planı Kurulamadı',
                html: buildAlakartGenerationErrorHtml(payload)
            });
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="relative w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900"
            >
                <div className="pointer-events-none absolute right-0 top-0 h-32 w-32 translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/10 blur-3xl" />

                <div className="relative z-10 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-100 text-3xl dark:bg-blue-900/30">
                        <span role="img" aria-label="tabak">🍽️</span>
                    </div>

                    {step === 'entry' ? (
                        <>
                            <h2 className="mb-2 text-2xl font-black leading-tight text-slate-900 dark:text-white">
                                Alakart Mutfağına Hoş Geldin
                            </h2>
                            <p className="mb-3 px-2 text-sm text-slate-500 dark:text-slate-400">
                                Şefim; bu menüde Alakart mutfağın için haftalık akşam yemeği oluşturabilirsin.
                            </p>
                            <p className="mb-6 px-2 text-xs font-medium text-slate-400 dark:text-slate-500">
                                İstersen sana başlangıç menüsü önerelim, istersen boş slotlarla başlayıp menüyü kendin kur.
                            </p>

                            <div className="space-y-3">
                                <button
                                    onClick={() => openPreferences('system')}
                                    className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition-all hover:bg-blue-700 active:scale-95"
                                >
                                    <span className="material-symbols-outlined text-lg">auto_awesome</span>
                                    Başlangıç Menüsünü Öner
                                </button>
                                <button
                                    onClick={() => openPreferences('manual')}
                                    className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-sm font-bold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 active:scale-95 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                                >
                                    <span className="material-symbols-outlined text-lg">edit_calendar</span>
                                    Menüyü Kendim Yapıcam
                                </button>
                            </div>

                            <button
                                onClick={() => onCancel?.()}
                                className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-300"
                            >
                                Şimdilik Kapat
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={() => setStep('entry')}
                                className="mb-4 flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-slate-700 dark:hover:text-slate-200"
                            >
                                <span className="material-symbols-outlined text-sm">arrow_back</span>
                                Geri
                            </button>

                            <h2 className="mb-2 text-xl font-black leading-tight text-slate-900 dark:text-white">
                                Haftalık Plan Ayarları
                            </h2>

                            <p className="mb-6 px-2 text-sm text-slate-500 dark:text-slate-400">
                                {generationMode === 'manual'
                                    ? 'Boş slotları hazırlamadan önce tercihlerini belirleyelim.'
                                    : 'Plan oluşturulmadan önce tercihlerini belirleyelim.'}
                            </p>

                            <div className="mb-6 space-y-4 text-left">
                                <div>
                                    <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-300">
                                        Kişi Sayısı
                                    </label>
                                    <div className="flex rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
                                        {[1, 2, 4, 6].map((person) => (
                                            <button
                                                key={person}
                                                onClick={() => setConfig((prev) => ({ ...prev, portion: person }))}
                                                className={`flex-1 rounded-lg py-2 text-sm font-bold transition-all ${config.portion === person
                                                    ? 'bg-white text-blue-600 shadow-sm dark:bg-slate-700'
                                                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'}`}
                                            >
                                                {person} Kişilik
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-300">
                                        Menü İçeriği
                                    </label>
                                    <p className="mb-2 text-[10px] text-slate-400">Hangi öğünler planlansın?</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {CATEGORY_OPTIONS.map((cat) => (
                                            <button
                                                key={cat.id}
                                                onClick={() => {
                                                    setConfig((prev) => {
                                                        const nextCategories = prev.categories.includes(cat.id)
                                                            ? prev.categories.filter((value) => value !== cat.id)
                                                            : [...prev.categories, cat.id];
                                                        return { ...prev, categories: nextCategories };
                                                    });
                                                }}
                                                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold transition-all ${config.categories.includes(cat.id)
                                                    ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                                                    : 'border-slate-200 bg-white text-slate-500 dark:border-slate-700 dark:bg-slate-800'}`}
                                            >
                                                <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${config.categories.includes(cat.id)
                                                    ? 'border-blue-500 bg-blue-500'
                                                    : 'border-slate-300'}`}>
                                                    {config.categories.includes(cat.id) ? (
                                                        <span className="material-symbols-outlined text-[10px] text-white">check</span>
                                                    ) : null}
                                                </span>
                                                {cat.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleCreatePlan}
                                disabled={creating || config.categories.length === 0}
                                className="relative flex h-14 w-full items-center rounded-xl bg-blue-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition-all hover:bg-blue-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-70"
                            >
                                {creating ? (
                                    <>
                                        {/* Spinner — sol taraf sabit */}
                                        <div className="shrink-0 h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                        {/* Dönen adım yazısı — sağ taraf */}
                                        <span
                                            className="ml-3 flex-1 text-left text-[13px] font-semibold leading-tight tracking-tight transition-opacity duration-200"
                                            style={{ opacity: stepVisible ? 1 : 0 }}
                                        >
                                            {GENERATION_STEPS[stepIndex]}
                                        </span>
                                    </>
                                ) : (
                                    <span className="flex w-full items-center justify-center gap-2">
                                        <span className="material-symbols-outlined text-lg">
                                            {generationMode === 'manual' ? 'calendar_add_on' : 'auto_awesome'}
                                        </span>
                                        {generationMode === 'manual' ? 'Boş Planla Başla' : 'Planı Oluştur'}
                                    </span>
                                )}
                            </button>

                            <p className="mt-4 text-[10px] text-slate-400">
                                {generationMode === 'manual'
                                    ? '*Seçtiğin slotlar boş olarak hazırlanacak, tarifleri sen yerleştireceksin.'
                                    : '*Tercihlerine ve stoğuna göre 7 günlük plan oluşturulacak.'}
                            </p>
                        </>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default AlakartPhaseZeroSurvey;
